import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    buildChannelRow,
    compactChannelName,
    dividePercent,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartMultiplier,
    formatChartNumber,
    KpiCard,
    MetricHelpLabel,
    MetricBasis,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    snapshotCompletenessAlert,
} from "./ReportingLab.jsx";

export default function ReportingProfitabilityLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const renderRatioValue = (value) => formatChartMultiplier(value);
    const [shopPerformance, setShopPerformance] = useState(null);
    const [channelData, setChannelData] = useState([]);
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setShopPerformance(null);
        setChannelData([]);
        setSnapshot(null);

        const erpApi = new ErpCalculationApi(getAuthorityConfiguration());

        erpApi.reportingOverview({projectId: project.id, erpFilter})
            .then((result) => {
                if (!active) {
                    return;
                }

                setShopPerformance(result.shopPerformance);
                setChannelData((result.channels?.items ?? []).map((item) => ({
                    channel: {id: item.id, name: item.name},
                    overview: item.overview,
                })));
                setSnapshot(result.snapshot);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const channelRows = useMemo(() => {
        return channelData
            .map(({channel, overview}) => buildChannelRow(channel, overview, MetricBasis.Completed))
            .filter((row) => moneyAmount(row.spendMarketing) > 0 || moneyAmount(row.turnover) > 0 || row.orders > 0)
            .sort((a, b) => moneyAmount(b.profit) - moneyAmount(a.profit));
    }, [channelData]);

    const turnover = shopPerformance?.orderTurnoverNet;
    const profit = shopPerformance?.orderProfitNet;
    const spendMarketing = shopPerformance?.orderSpendMarketing;
    const spendGoods = shopPerformance?.orderSpendGoodsNet;
    const margin = dividePercent(moneyAmount(profit), moneyAmount(turnover));
    const pno = dividePercent(moneyAmount(spendMarketing), moneyAmount(turnover));
    const marginBeforeMarketing = moneyAmount(turnover) - moneyAmount(spendGoods);
    const poas = dividePercent(marginBeforeMarketing, moneyAmount(spendMarketing));
    const currency = profit?.currency ?? turnover?.currency ?? projectCurrency(project);
    const topProfitChannels = channelRows.slice(0, 8);
    const lossChannels = [...channelRows].filter((row) => moneyAmount(row.profit) < 0 || (row.poas !== null && row.poas < 100)).sort((a, b) => moneyAmount(a.profit) - moneyAmount(b.profit)).slice(0, 6);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const profitabilityBridgeOption = {
        textStyle: chartTextStyle,
        grid: {left: 18, right: 18, top: 24, bottom: 42, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "category",
            data: [
                t("reporting_lab.kpi.revenue", {defaultValue: "Obrat"}),
                t("reporting_lab.table.purchase", {defaultValue: "Nákup"}),
                t("reporting_lab.product_profitability.margin_before_marketing_short", {defaultValue: "Marže zboží"}),
                t("reporting_lab.table.marketing", {defaultValue: "Marketing"}),
                t("reporting_lab.table.contribution", {defaultValue: "Marže"}),
            ],
            axisLabel: {fontSize: 12},
        },
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: formatChartNumber}},
        series: [{
            type: "bar",
            data: [
                {value: moneyToMajor(turnover), itemStyle: {color: "#2563eb"}},
                {value: -moneyToMajor(spendGoods), itemStyle: {color: "#f97316"}},
                {value: marginBeforeMarketing / 100, itemStyle: {color: "#0ea5e9"}},
                {value: -moneyToMajor(spendMarketing), itemStyle: {color: "#7c3aed"}},
                {value: moneyToMajor(profit), itemStyle: {color: moneyAmount(profit) < 0 ? "#e11d48" : "#16a34a"}},
            ],
            barMaxWidth: 64,
            label: {show: true, position: "top", formatter: (params) => formatChartCurrency(params.value, currency)},
        }],
    };

    const profitByChannelOption = {
        color: ["#16a34a", "#7c3aed"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 24, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {type: "category", data: topProfitChannels.map((row) => compactChannelName(row.channel))},
        yAxis: {
            type: "value",
            axisLabel: {
                fontSize: 12,
                formatter: formatChartNumber,
            },
        },
        series: [
            {
                name: t("reporting_lab.series.profit", {defaultValue: "Marže"}),
                type: "bar",
                data: topProfitChannels.map((row) => moneyToMajor(row.profit)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.series.marketing", {defaultValue: "Marketing"}),
                type: "bar",
                data: topProfitChannels.map((row) => moneyToMajor(row.spendMarketing)),
                barMaxWidth: 28,
            },
        ],
    };

    const poasOption = {
        color: ["#2563eb"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 20, bottom: 54, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, formatChartMultiplier),
        },
        xAxis: {type: "category", data: channelRows.slice(0, 10).map((row) => compactChannelName(row.channel))},
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartMultiplier},
        },
        series: [
            {
                name: "POAS",
                type: "bar",
                data: channelRows.slice(0, 10).map((row) => row.poas ?? 0),
                barMaxWidth: 30,
                markLine: {
                    symbol: "none",
                    data: [{yAxis: 100, name: "Break-even"}],
                    label: {formatter: "Break-even 1,0x"},
                },
            },
        ],
    };

    return <WideSecureLayout
        title={t("reporting_lab.pages.profitability", {defaultValue: "Ziskovost"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[snapshotCompletenessAlert(snapshot, t)]}
    >
        <div className="grid grid-cols-12 gap-4">
                <ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.profitability.title", {defaultValue: "K čemu slouží ziskovost"})}
                >
                    {t("reporting_lab.descriptions.profitability.text", {defaultValue: "Manažerský pohled na dokončený obrat, zisk, marži, marketingové náklady a kanály, které nejvíce pomáhají nebo poškozují profitabilitu."})}
                </ReportingPageDescription>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.revenue", {defaultValue: "Obrat"})} value={renderCurrencyValue(turnover, true)} meta={t("reporting_lab.metric_basis.business.short_label", {defaultValue: "Dokončené"})}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.profit", {defaultValue: "Marže"})} value={renderCurrencyValue(profit, true)} meta={`${t("reporting_lab.kpi.margin", {defaultValue: "Marže"})} ${renderPercentValue(margin)}`} tone={moneyAmount(profit) < 0 ? "bad" : "default"}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.table.purchase", {defaultValue: "Nákup"})} value={renderCurrencyValue(spendGoods, true)} meta={t("reporting_lab.profitability.purchase_price_goods", {defaultValue: "Nákupní cena zboží"})}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard
                        loading={loading}
                        label="POAS"
                        value={renderRatioValue(poas)}
                        meta={t("reporting_lab.profitability.profit_per_ad_spend", {defaultValue: "Marže zboží vůči marketingu"})}
                        helper={t("reporting_lab.metrics_help.poas", {defaultValue: "Marže zboží dělená marketingovými náklady."})}
                        tone={poas < 100 ? "bad" : "default"}
                    />
                </div>

                <Panel loading={loading} title={t("reporting_lab.profitability.financial_bridge", {defaultValue: "Od obratu k marži"})} className="col-span-12">
                    <EChart option={profitabilityBridgeOption} className="h-80"/>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.profitability.profit_by_channel", {defaultValue: "Marže a marketing podle kanálu"})} className="col-span-12 xl:col-span-7">
                    <EChart option={profitByChannelOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.profitability.poas_by_channel", {defaultValue: "POAS podle kanálu"})} className="col-span-12 xl:col-span-5">
                    <EChart option={poasOption} className="h-96"/>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.profitability.channels_to_check", {defaultValue: "Kanály ke kontrole ziskovosti"})} className="col-span-12 xl:col-span-4">
                    <div className="space-y-3">
                        {lossChannels.map((row) => <div key={row.channel.id} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                            <div className="flex items-center justify-between gap-3">
                                <div className="font-semibold text-slate-900 dark:text-slate-100">{compactChannelName(row.channel)}</div>
                                <div className="text-sm font-semibold text-rose-600">{renderRatioValue(row.poas)}</div>
                            </div>
                            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-500 dark:text-slate-400">
                                <div>{t("reporting_lab.review.spend", {defaultValue: "Spend"})} {renderCurrencyValue(row.spendMarketing, true)}</div>
                                <div className="text-right">{t("reporting_lab.review.profit", {defaultValue: "Marže"})} {renderCurrencyValue(row.profit, true)}</div>
                            </div>
                        </div>)}
                        {lossChannels.length === 0 && <div className="text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.profitability.no_loss_channels", {defaultValue: "Pro vybrané období nejsou vidět ztrátové nebo podlimitní kanály."})}</div>}
                    </div>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.profitability.table", {defaultValue: "Marže podle kanálu"})} className="col-span-12 xl:col-span-8">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.table.channel", {defaultValue: "Kanál"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold"><MetricHelpLabel label={t("reporting_lab.table.contribution", {defaultValue: "Marže"})} tooltip={t("reporting_lab.table.contribution_help", {defaultValue: "Marže = obrat přiřazených objednávek minus nákupní cena zboží a marketingové náklady. Nejde o čistý účetní zisk; nezahrnuje interní náklady dopravy, plateb ani režii."})}/></th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.table.marketing", {defaultValue: "Marketing"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">Marže</th>
                                <th className="py-2 pr-4 text-right font-semibold">PNO</th>
                                <th className="py-2 text-right font-semibold">POAS</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {channelRows.map((row) => <tr key={row.channel.id}>
                                <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{compactChannelName(row.channel)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.turnover, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.profit, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.spendMarketing, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.margin)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.pno)}</td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderRatioValue(row.poas)}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>
    </WideSecureLayout>;
}
